import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Alert, { AlertProps } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';

import {
  requestPasswordResetMutation,
  resendEmailVerificationMutation,
} from '@drivingdoc/api-client/generated/react-query';

import TextInput from 'components/FormInputs/TextInput';

import { useExternalMode } from 'hooks/useExternalMode';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

const formSchema = z.object({
  email: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

function Form(): JSX.Element {
  const [alertData, setAlertData] = useState<{ message: string; severity: AlertProps['severity'] } | null>(null);
  const { isExternalMode } = useExternalMode();

  const forgotPassword = useMutation({
    ...requestPasswordResetMutation(),
    onSuccess: () => {
      setAlertData({
        message:
          'If an account with that email exists, a password reset link has been sent. Please check your inbox and spam folder. The link will expire in 24 hours.',
        severity: 'success',
      });
    },
    onError: (error) => {
      // Check if the error is due to unverified email
      if (error.response?.data?.error?.code === 'EMAIL_NOT_VERIFIED') {
        setAlertData({
          message: 'Your email address has not been verified. Please verify your email before resetting your password.',
          severity: 'warning',
        });
      } else {
        setAlertData({
          message: getApiErrorMessage(error),
          severity: 'error',
        });
      }
    },
  });

  const resendVerification = useMutation({
    ...resendEmailVerificationMutation(),
    onSuccess: () => {
      setAlertData({
        message: 'Verification email has been resent. Please check your inbox and spam folder.',
        severity: 'success',
      });

      // reset forgot password mutation to remove the error
      forgotPassword.reset();
    },
    onError: (error) => {
      setAlertData({
        message: getApiErrorMessage(error),
        severity: 'error',
      });
    },
  });

  // Derive emailNotVerified from the forgotPassword error
  const emailNotVerified = forgotPassword.error?.response?.data?.error?.code === 'EMAIL_NOT_VERIFIED';

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const { handleSubmit, watch } = form;

  const onSubmit = async ({ email }: FormValues) => {
    forgotPassword.mutate({
      body: {
        email,
      },
    });
  };

  const handleResendVerification = () => {
    const email = watch('email');
    if (email) {
      resendVerification.mutate({
        body: {
          email,
        },
      });
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Box>
            <Typography
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'medium',
              }}
              gutterBottom
              color={'text.secondary'}
            >
              Recover account
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              Forgot your password?
            </Typography>
            <Typography color="text.secondary">
              Enter your email address below and we&apos;ll get you back on track.
            </Typography>
          </Box>

          <Collapse in={!!alertData && !forgotPassword.isPending && !resendVerification.isPending}>
            <Alert
              variant="outlined"
              severity={alertData?.severity}
              action={
                emailNotVerified ? (
                  <Button color="inherit" size="small" onClick={handleResendVerification}>
                    Resend Verification
                  </Button>
                ) : undefined
              }
            >
              {alertData?.message}
            </Alert>
          </Collapse>

          <TextInput name="email" label="Email" />

          <Collapse in={forgotPassword.isPending || resendVerification.isPending}>
            <LinearProgress />
          </Collapse>

          <Stack
            gap={2}
            flexDirection={{ xs: 'column-reverse', sm: 'row' }}
            alignItems={{ xs: 'stretched', sm: 'center' }}
            justifyContent={'space-between'}
          >
            <Button
              component={RouterLink}
              size="large"
              variant="outlined"
              to={isExternalMode ? '/external/login' : '/login'}
            >
              Back to login
            </Button>
            <Button
              size="large"
              variant="contained"
              type="submit"
              disabled={forgotPassword.isPending || resendVerification.isPending}
            >
              Send reset link
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
}

export default Form;
